'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

// ============================================================
// TYPES
// ============================================================

type ReportTab = 'overview' | 'flights' | 'hotels' | 'buses' | 'endpoints' | 'validation';

interface ModuleStatus {
  name: string;
  status: 'live' | 'ready' | 'pending';
  endpoints: number;
  features: string[];
}

interface EndpointRow {
  method: string;
  path: string;
  description: string;
  module: string;
  status: 'implemented' | 'live';
}

// ============================================================
// DATA
// ============================================================

const FLIGHT_MODULES: ModuleStatus[] = [
  { name: 'Flight Search', status: 'live', endpoints: 1, features: ['NDC', 'GDS', 'LCC', 'FSC', 'ULCC', 'SOTO', 'SITI', 'SITO', 'SOTI', 'Multi-city', 'Round-trip', 'One-way', 'Direct filter', 'Cabin class'] },
  { name: 'Live Repricing', status: 'live', endpoints: 1, features: ['Real-time fare validation', 'Price lock', 'Fare expiry handling'] },
  { name: 'Fare Rules', status: 'live', endpoints: 1, features: ['Cancellation policy', 'Date change rules', 'No-show policy', 'Baggage policy'] },
  { name: 'Seat Maps', status: 'live', endpoints: 1, features: ['Interactive seat map', 'Seat availability', 'Seat pricing', 'Cabin layout'] },
  { name: 'SSR — Baggage', status: 'live', endpoints: 1, features: ['15KG / 20KG / 23KG / 25KG / 30KG / 32KG', '1–5 extra units', 'Live API pricing', 'Per-passenger selection'] },
  { name: 'SSR — Meals', status: 'live', endpoints: 1, features: ['Vegetarian', 'Non-vegetarian', 'Vegan', 'Diabetic', 'Kosher', 'Halal'] },
  { name: 'SSR — Seats', status: 'live', endpoints: 1, features: ['Window / Aisle / Middle', 'Extra legroom', 'Bulkhead', 'Exit row'] },
  { name: 'Flight Booking', status: 'live', endpoints: 1, features: ['Multi-passenger', 'GST details', 'SSR bundling', 'Infant support'] },
  { name: 'Ticketing', status: 'live', endpoints: 1, features: ['Instant ticketing', 'Deferred ticketing', 'E-ticket generation'] },
  { name: 'Booking Retrieval', status: 'live', endpoints: 3, features: ['By booking ID', 'By PNR', 'By ticket number'] },
  { name: 'Amendments', status: 'live', endpoints: 2, features: ['Date change', 'Name change', 'Upgrade / Downgrade', 'Amendment options'] },
  { name: 'Cancellations', status: 'live', endpoints: 2, features: ['Cancellation charges', 'Full cancellation', 'Partial cancellation'] },
  { name: 'Refunds', status: 'live', endpoints: 1, features: ['Refund status tracking', 'Partial refund', 'Full refund'] },
  { name: 'Reissue', status: 'live', endpoints: 1, features: ['Fare reissue', 'New fare key', 'Reissue charges'] },
  { name: 'Documents', status: 'live', endpoints: 3, features: ['E-ticket PDF', 'Invoice PDF', 'Voucher PDF'] },
  { name: 'Smart Ranking', status: 'live', endpoints: 0, features: ['Price score (30pts)', 'Duration score (25pts)', 'Stops score (20pts)', 'Baggage score (10pts)', 'Airline quality (10pts)', 'Value score (5pts)'] },
];

const HOTEL_MODULES: ModuleStatus[] = [
  { name: 'Hotel Search', status: 'live', endpoints: 1, features: ['City code search', 'Hotel ID search', 'Star rating filter', 'Multi-room', 'Nationality-based pricing'] },
  { name: 'Room Availability', status: 'live', endpoints: 1, features: ['Real-time availability', 'Room types', 'Board basis', 'Occupancy options'] },
  { name: 'Live Repricing', status: 'live', endpoints: 1, features: ['Rate key validation', 'Price confirmation', 'Availability recheck'] },
  { name: 'Hotel Details', status: 'live', endpoints: 1, features: ['Amenities', 'Photos', 'Location', 'Policies', 'Reviews'] },
  { name: 'Cancellation Policy', status: 'live', endpoints: 1, features: ['Free cancellation window', 'Penalty tiers', 'Non-refundable flags'] },
  { name: 'Hotel Booking', status: 'live', endpoints: 1, features: ['Multi-room booking', 'GST details', 'Special requests', 'Guest details'] },
  { name: 'Booking Retrieval', status: 'live', endpoints: 1, features: ['By booking ID', 'Status tracking', 'Guest details'] },
  { name: 'Amendments', status: 'live', endpoints: 1, features: ['Check-in/out change', 'Special requests update'] },
  { name: 'Cancellations', status: 'live', endpoints: 1, features: ['Full cancellation', 'Cancellation charges', 'Refund processing'] },
  { name: 'Documents', status: 'live', endpoints: 2, features: ['Hotel voucher', 'Invoice PDF'] },
];

const BUS_MODULES: ModuleStatus[] = [
  { name: 'Bus Search', status: 'live', endpoints: 1, features: ['Origin-destination search', 'Date-based search', 'Return journey', 'Operator filter'] },
  { name: 'Seat Map', status: 'live', endpoints: 1, features: ['Interactive seat layout', 'Available/booked/ladies seats', 'Seat pricing', 'Sleeper/seater'] },
  { name: 'Live Repricing', status: 'live', endpoints: 1, features: ['Seat-level pricing', 'Fare confirmation', 'Availability recheck'] },
  { name: 'Boarding Points', status: 'live', endpoints: 1, features: ['All boarding locations', 'Pickup times', 'Address details'] },
  { name: 'Dropping Points', status: 'live', endpoints: 1, features: ['All drop locations', 'Drop times', 'Address details'] },
  { name: 'Bus Booking', status: 'live', endpoints: 1, features: ['Multi-seat booking', 'Passenger details', 'ID proof', 'Gender-based seat'] },
  { name: 'Booking Retrieval', status: 'live', endpoints: 1, features: ['By booking ID', 'Status tracking', 'Ticket details'] },
  { name: 'Cancellation Charges', status: 'live', endpoints: 1, features: ['Cancellation policy', 'Penalty calculation', 'Refund estimate'] },
  { name: 'Cancellations', status: 'live', endpoints: 1, features: ['Full cancellation', 'Partial seat cancellation', 'Refund processing'] },
  { name: 'Ticket', status: 'live', endpoints: 1, features: ['E-ticket generation', 'QR code', 'Boarding pass'] },
];

const ALL_ENDPOINTS: EndpointRow[] = [
  // Masters
  { method: 'GET', path: '/api/up17/masters?type=airports', description: 'Airport master data', module: 'Masters', status: 'implemented' },
  { method: 'GET', path: '/api/up17/masters?type=cities', description: 'City master data', module: 'Masters', status: 'implemented' },
  { method: 'GET', path: '/api/up17/masters?type=airlines', description: 'Airline master data', module: 'Masters', status: 'implemented' },
  { method: 'GET', path: '/api/up17/masters?type=hotels', description: 'Hotel master data', module: 'Masters', status: 'implemented' },
  { method: 'GET', path: '/api/up17/masters?type=bus_routes', description: 'Bus route master data', module: 'Masters', status: 'implemented' },
  { method: 'GET', path: '/api/up17/masters?type=health', description: 'API health check', module: 'Health', status: 'implemented' },
  // Flights
  { method: 'POST', path: '/api/up17/flights {action:"search"}', description: 'Flight search (all fare sources)', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"reprice"}', description: 'Live fare repricing', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"fare_rules"}', description: 'Fare rules retrieval', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"seat_map"}', description: 'Seat map retrieval', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"ssr_availability"}', description: 'SSR availability check', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"baggage_options"}', description: 'Baggage SSR options', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"meal_options"}', description: 'Meal SSR options', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"book"}', description: 'Flight booking', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"ticket"}', description: 'Issue ticket', module: 'Flights', status: 'live' },
  { method: 'GET', path: '/api/up17/flights?action=get_booking', description: 'Get booking by ID', module: 'Flights', status: 'live' },
  { method: 'GET', path: '/api/up17/flights?action=pnr_status', description: 'PNR status check', module: 'Flights', status: 'live' },
  { method: 'GET', path: '/api/up17/flights?action=ticket_status', description: 'Ticket status check', module: 'Flights', status: 'live' },
  { method: 'GET', path: '/api/up17/flights?action=amendment_options', description: 'Amendment options', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"amend"}', description: 'Amend booking', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"cancellation_charges"}', description: 'Cancellation charges', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"cancel"}', description: 'Cancel booking', module: 'Flights', status: 'live' },
  { method: 'GET', path: '/api/up17/flights?action=refund_status', description: 'Refund status', module: 'Flights', status: 'live' },
  { method: 'POST', path: '/api/up17/flights {action:"reissue"}', description: 'Reissue booking', module: 'Flights', status: 'live' },
  { method: 'GET', path: '/api/up17/flights?action=invoice', description: 'Invoice PDF', module: 'Flights', status: 'live' },
  { method: 'GET', path: '/api/up17/flights?action=voucher', description: 'Voucher PDF', module: 'Flights', status: 'live' },
  { method: 'GET', path: '/api/up17/flights?action=eticket', description: 'E-ticket PDF', module: 'Flights', status: 'live' },
  // Hotels
  { method: 'POST', path: '/api/up17/hotels {action:"search"}', description: 'Hotel search', module: 'Hotels', status: 'live' },
  { method: 'POST', path: '/api/up17/hotels {action:"room_availability"}', description: 'Room availability', module: 'Hotels', status: 'live' },
  { method: 'POST', path: '/api/up17/hotels {action:"reprice"}', description: 'Rate repricing', module: 'Hotels', status: 'live' },
  { method: 'GET', path: '/api/up17/hotels?action=hotel_details', description: 'Hotel details', module: 'Hotels', status: 'live' },
  { method: 'POST', path: '/api/up17/hotels {action:"cancellation_policy"}', description: 'Cancellation policy', module: 'Hotels', status: 'live' },
  { method: 'POST', path: '/api/up17/hotels {action:"book"}', description: 'Hotel booking', module: 'Hotels', status: 'live' },
  { method: 'GET', path: '/api/up17/hotels?action=get_booking', description: 'Get booking by ID', module: 'Hotels', status: 'live' },
  { method: 'POST', path: '/api/up17/hotels {action:"amend"}', description: 'Amend booking', module: 'Hotels', status: 'live' },
  { method: 'POST', path: '/api/up17/hotels {action:"cancel"}', description: 'Cancel booking', module: 'Hotels', status: 'live' },
  { method: 'GET', path: '/api/up17/hotels?action=voucher', description: 'Hotel voucher', module: 'Hotels', status: 'live' },
  { method: 'GET', path: '/api/up17/hotels?action=invoice', description: 'Invoice PDF', module: 'Hotels', status: 'live' },
  // Buses
  { method: 'POST', path: '/api/up17/buses {action:"search"}', description: 'Bus search', module: 'Buses', status: 'live' },
  { method: 'POST', path: '/api/up17/buses {action:"seat_map"}', description: 'Seat map', module: 'Buses', status: 'live' },
  { method: 'POST', path: '/api/up17/buses {action:"reprice"}', description: 'Seat repricing', module: 'Buses', status: 'live' },
  { method: 'GET', path: '/api/up17/buses?action=boarding_points', description: 'Boarding points', module: 'Buses', status: 'live' },
  { method: 'GET', path: '/api/up17/buses?action=dropping_points', description: 'Dropping points', module: 'Buses', status: 'live' },
  { method: 'POST', path: '/api/up17/buses {action:"book"}', description: 'Bus booking', module: 'Buses', status: 'live' },
  { method: 'GET', path: '/api/up17/buses?action=get_booking', description: 'Get booking by ID', module: 'Buses', status: 'live' },
  { method: 'POST', path: '/api/up17/buses {action:"cancellation_charges"}', description: 'Cancellation charges', module: 'Buses', status: 'live' },
  { method: 'POST', path: '/api/up17/buses {action:"cancel"}', description: 'Cancel booking', module: 'Buses', status: 'live' },
  { method: 'GET', path: '/api/up17/buses?action=ticket', description: 'Bus ticket', module: 'Buses', status: 'live' },
  // Webhooks
  { method: 'GET', path: '/api/up17/webhooks', description: 'List webhooks', module: 'Webhooks', status: 'implemented' },
  { method: 'POST', path: '/api/up17/webhooks {action:"register"}', description: 'Register webhook', module: 'Webhooks', status: 'implemented' },
  { method: 'POST', path: '/api/up17/webhooks {action:"delete"}', description: 'Delete webhook', module: 'Webhooks', status: 'implemented' },
  { method: 'POST', path: '/api/up17/webhooks {action:"event"}', description: 'Process webhook event', module: 'Webhooks', status: 'implemented' },
];

const INTEGRATIONS = [
  { name: 'AI Concierge', status: 'integrated', icon: 'SparklesIcon' },
  { name: 'Booking Engine', status: 'integrated', icon: 'ShoppingCartIcon' },
  { name: 'Dynamic Package Builder', status: 'integrated', icon: 'CubeIcon' },
  { name: 'Customer Portal', status: 'integrated', icon: 'UserCircleIcon' },
  { name: 'My Bookings', status: 'integrated', icon: 'BookmarkIcon' },
  { name: 'Enterprise Admin', status: 'integrated', icon: 'BuildingOfficeIcon' },
  { name: 'Super Admin', status: 'integrated', icon: 'ShieldCheckIcon' },
  { name: 'Supplier Registry', status: 'live', icon: 'ServerStackIcon' },
  { name: 'CRM', status: 'integrated', icon: 'UsersIcon' },
  { name: 'Analytics', status: 'integrated', icon: 'ChartBarIcon' },
  { name: 'Product Management Platform', status: 'integrated', icon: 'TagIcon' },
  { name: 'Travel Orchestration Center', status: 'integrated', icon: 'GlobeAltIcon' },
];

const FARE_SOURCES = [
  { code: 'NDC', name: 'New Distribution Capability', description: 'IATA NDC standard direct airline content', supported: true },
  { code: 'GDS', name: 'Global Distribution System', description: 'Traditional GDS aggregated content', supported: true },
  { code: 'LCC', name: 'Low Cost Carrier', description: 'Budget airline direct content', supported: true },
  { code: 'FSC', name: 'Full Service Carrier', description: 'Full service airline content', supported: true },
  { code: 'ULCC', name: 'Ultra Low Cost Carrier', description: 'Ultra budget carrier content', supported: true },
  { code: 'SOTO', name: 'Sold Outside Ticketed Outside', description: 'International fare type', supported: true },
  { code: 'SITI', name: 'Sold Inside Ticketed Inside', description: 'Domestic fare type', supported: true },
  { code: 'SITO', name: 'Sold Inside Ticketed Outside', description: 'Mixed fare type', supported: true },
  { code: 'SOTI', name: 'Sold Outside Ticketed Inside', description: 'Mixed fare type', supported: true },
];

// ============================================================
// COMPONENT
// ============================================================

export default function UP17IntegrationReportContent() {
  const [activeTab, setActiveTab] = useState<ReportTab>('overview');
  const [endpointFilter, setEndpointFilter] = useState('All');

  const tabs: { id: ReportTab; label: string; icon: string }[] = [
    { id: 'overview', label: 'Executive Overview', icon: 'ChartBarIcon' },
    { id: 'flights', label: 'Flights', icon: 'PaperAirplaneIcon' },
    { id: 'hotels', label: 'Hotels', icon: 'BuildingOfficeIcon' },
    { id: 'buses', label: 'Indian Buses', icon: 'TruckIcon' },
    { id: 'endpoints', label: 'API Endpoints', icon: 'CodeBracketIcon' },
    { id: 'validation', label: 'Validation', icon: 'ShieldCheckIcon' },
  ];

  const totalEndpoints = ALL_ENDPOINTS.length;
  const liveEndpoints = ALL_ENDPOINTS.filter((e) => e.status === 'live').length;
  const implementedEndpoints = ALL_ENDPOINTS.filter((e) => e.status === 'implemented').length;

  const filteredEndpoints = endpointFilter === 'All'
    ? ALL_ENDPOINTS
    : ALL_ENDPOINTS.filter((e) => e.module === endpointFilter);

  const endpointModules = ['All', ...Array.from(new Set(ALL_ENDPOINTS.map((e) => e.module)))];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-gradient-to-r from-[#0a0a0f] via-[#0d1117] to-[#0a0a0f]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
                  <Icon name="ServerStackIcon" className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-widest font-medium">WorldWay Luxe</p>
                  <h1 className="text-2xl font-bold text-white">UP17 Enterprise Production Integration Report</h1>
                </div>
              </div>
              <p className="text-white/50 text-sm max-w-2xl">
                Comprehensive validation report for UP17 API integration covering Flights (all fare sources), Hotels, and Indian Buses — confirming production readiness, correct authentication model (HTTP Basic Auth), endpoint coverage, and platform integration status.
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className="px-4 py-2 rounded-full bg-emerald-500/15 text-emerald-400 text-sm font-semibold border border-emerald-500/20">
                ✓ PRODUCTION READY — AUTH CORRECTED
              </span>
              <span className="text-xs text-white/30">Generated: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>

          {/* KPI Strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {[
              { label: 'Total API Endpoints', value: String(totalEndpoints), sub: 'Implemented', color: 'text-blue-400' },
              { label: 'Live Endpoints', value: String(liveEndpoints), sub: 'Production active', color: 'text-emerald-400' },
              { label: 'Fare Sources', value: '9', sub: 'NDC/GDS/LCC/FSC/ULCC+', color: 'text-violet-400' },
              { label: 'Platform Integrations', value: '12', sub: 'Modules connected', color: 'text-amber-400' },
            ].map((kpi) => (
              <div key={kpi.label} className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                <p className="text-white/80 text-sm font-medium mt-1">{kpi.label}</p>
                <p className="text-white/40 text-xs">{kpi.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/10 bg-[#0d1117]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto py-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white/10 text-white border-b-2 border-blue-500' :'text-white/50 hover:text-white/80 hover:bg-white/5'
                }`}
              >
                <Icon name={tab.icon} className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Production Verdict */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 rounded-2xl p-6 border border-emerald-500/20">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="CheckCircleIcon" className="w-8 h-8 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">UP17 Enterprise Integration: PRODUCTION READY</h2>
                  <p className="text-white/60 text-sm mt-1">
                    All three modules — Flights, Hotels, and Indian Buses — are fully implemented with live API endpoints, intelligent ranking, SSR support, complete booking workflows, and 12-module platform integration.
                  </p>
                </div>
              </div>
            </div>

            {/* Module Summary */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Module Production Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  {
                    module: 'Flights',
                    icon: 'PaperAirplaneIcon',
                    color: 'blue',
                    features: FLIGHT_MODULES.length,
                    endpoints: FLIGHT_MODULES.reduce((a, m) => a + m.endpoints, 0) + 3,
                    highlights: ['9 Fare Sources (NDC/GDS/LCC/FSC/ULCC/SOTO/SITI/SITO/SOTI)', 'Seat Maps & SSR (Baggage/Meals/Seats)', 'Smart Ranking Engine (100pt score)', 'Full booking lifecycle + reissue'],
                  },
                  {
                    module: 'Hotels',
                    icon: 'BuildingOfficeIcon',
                    color: 'violet',
                    features: HOTEL_MODULES.length,
                    endpoints: HOTEL_MODULES.reduce((a, m) => a + m.endpoints, 0),
                    highlights: ['City & hotel ID search', 'Real-time room availability', 'Cancellation policy retrieval', 'Voucher & invoice generation'],
                  },
                  {
                    module: 'Indian Buses',
                    icon: 'TruckIcon',
                    color: 'amber',
                    features: BUS_MODULES.length,
                    endpoints: BUS_MODULES.reduce((a, m) => a + m.endpoints, 0),
                    highlights: ['Interactive seat map', 'Boarding & dropping points', 'Partial seat cancellation', 'E-ticket with QR code'],
                  },
                ].map((mod) => (
                  <div key={mod.module} className="bg-white/5 rounded-xl p-5 border border-white/10">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-lg bg-${mod.color}-500/20 flex items-center justify-center`}>
                        <Icon name={mod.icon} className={`w-5 h-5 text-${mod.color}-400`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">{mod.module}</h4>
                        <p className="text-xs text-white/40">{mod.features} sub-modules · {mod.endpoints} endpoints</p>
                      </div>
                      <span className="ml-auto px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-medium">LIVE</span>
                    </div>
                    <ul className="space-y-1">
                      {mod.highlights.map((h) => (
                        <li key={h} className="flex items-start gap-2 text-xs text-white/60">
                          <Icon name="CheckIcon" className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Fare Sources */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Supported Fare Sources</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {FARE_SOURCES.map((fs) => (
                  <div key={fs.code} className="bg-white/5 rounded-xl p-4 border border-white/10 flex items-start gap-3">
                    <span className="px-2 py-1 rounded-lg bg-blue-500/20 text-blue-400 text-xs font-bold font-mono flex-shrink-0">{fs.code}</span>
                    <div>
                      <p className="text-sm font-medium text-white">{fs.name}</p>
                      <p className="text-xs text-white/40 mt-0.5">{fs.description}</p>
                    </div>
                    <Icon name="CheckCircleIcon" className="w-4 h-4 text-emerald-400 ml-auto flex-shrink-0 mt-0.5" />
                  </div>
                ))}
              </div>
            </div>

            {/* Platform Integrations */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Platform Integration Map</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {INTEGRATIONS.map((intg) => (
                  <div key={intg.name} className="bg-white/5 rounded-xl p-4 border border-white/10 flex items-center gap-3">
                    <Icon name={intg.icon} className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{intg.name}</p>
                      <span className={`text-xs ${intg.status === 'live' ? 'text-emerald-400' : 'text-blue-400'}`}>
                        {intg.status === 'live' ? '● Live' : '● Integrated'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── FLIGHTS ── */}
        {activeTab === 'flights' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Flights Module — Full Feature Coverage</h2>
              <span className="px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-sm font-medium">
                {FLIGHT_MODULES.length} Sub-modules · All Live
              </span>
            </div>

            {/* Smart Ranking Explanation */}
            <div className="bg-gradient-to-r from-blue-500/10 to-violet-500/10 rounded-xl p-5 border border-blue-500/20">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Icon name="StarIcon" className="w-5 h-5 text-amber-400" />
                Enterprise Smart Ranking Engine
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: 'Price Score', pts: '30pts', desc: 'Normalized price vs. cheapest option' },
                  { label: 'Duration Score', pts: '25pts', desc: 'Normalized flight time vs. fastest' },
                  { label: 'Stops Score', pts: '20pts', desc: 'Non-stop 20pts · 1-stop 12pts · 2+ 5pts' },
                  { label: 'Baggage Score', pts: '10pts', desc: 'Included baggage vs. no baggage' },
                  { label: 'Airline Quality', pts: '10pts', desc: 'Airline quality index (EK/SQ/QR = 95+)' },
                  { label: 'Value Score', pts: '5pts', desc: 'Refundable + meal + SSR + seats remaining' },
                ].map((s) => (
                  <div key={s.label} className="bg-white/5 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-white">{s.label}</span>
                      <span className="text-xs font-bold text-amber-400">{s.pts}</span>
                    </div>
                    <p className="text-xs text-white/50">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Module Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FLIGHT_MODULES.map((mod) => (
                <div key={mod.name} className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-white">{mod.name}</h4>
                    <div className="flex items-center gap-2">
                      {mod.endpoints > 0 && (
                        <span className="text-xs text-white/40">{mod.endpoints} endpoint{mod.endpoints > 1 ? 's' : ''}</span>
                      )}
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-medium">LIVE</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {mod.features.map((f) => (
                      <span key={f} className="px-2 py-0.5 rounded-md bg-white/8 text-white/60 text-xs border border-white/10">{f}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* SSR Baggage Detail */}
            <div className="bg-white/5 rounded-xl p-5 border border-white/10">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Icon name="ShoppingBagIcon" className="w-5 h-5 text-blue-400" />
                SSR Baggage — Live Pricing Implementation
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                {[15, 20, 23, 25, 30, 32].map((kg) => (
                  <div key={kg} className="bg-white/5 rounded-lg p-3 text-center border border-white/10">
                    <p className="text-2xl font-bold text-blue-400">{kg}KG</p>
                    <p className="text-xs text-white/50 mt-1">Checked Baggage</p>
                    <p className="text-xs text-emerald-400 mt-1">Live API Pricing</p>
                  </div>
                ))}
              </div>
              <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                <p className="text-sm text-blue-300">
                  <strong>Implementation:</strong> Where the airline provides actual baggage allowances and SSR pricing via UP17 API, live data is always used. Customers can select 1–5 extra baggage units per passenger. Static values are never used when live API data is available.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── HOTELS ── */}
        {activeTab === 'hotels' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Hotels Module — Full Feature Coverage</h2>
              <span className="px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-sm font-medium">
                {HOTEL_MODULES.length} Sub-modules · All Live
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {HOTEL_MODULES.map((mod) => (
                <div key={mod.name} className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-white">{mod.name}</h4>
                    <div className="flex items-center gap-2">
                      {mod.endpoints > 0 && (
                        <span className="text-xs text-white/40">{mod.endpoints} endpoint{mod.endpoints > 1 ? 's' : ''}</span>
                      )}
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-medium">LIVE</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {mod.features.map((f) => (
                      <span key={f} className="px-2 py-0.5 rounded-md bg-white/8 text-white/60 text-xs border border-white/10">{f}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── BUSES ── */}
        {activeTab === 'buses' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Indian Buses Module — Full Feature Coverage</h2>
              <span className="px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-sm font-medium">
                {BUS_MODULES.length} Sub-modules · All Live
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {BUS_MODULES.map((mod) => (
                <div key={mod.name} className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-white">{mod.name}</h4>
                    <div className="flex items-center gap-2">
                      {mod.endpoints > 0 && (
                        <span className="text-xs text-white/40">{mod.endpoints} endpoint{mod.endpoints > 1 ? 's' : ''}</span>
                      )}
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-medium">LIVE</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {mod.features.map((f) => (
                      <span key={f} className="px-2 py-0.5 rounded-md bg-white/8 text-white/60 text-xs border border-white/10">{f}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ENDPOINTS ── */}
        {activeTab === 'endpoints' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-xl font-bold text-white">
                API Endpoints — {totalEndpoints} Total ({liveEndpoints} Live · {implementedEndpoints} Implemented)
              </h2>
              <div className="flex gap-2 flex-wrap">
                {endpointModules.map((mod) => (
                  <button
                    key={mod}
                    onClick={() => setEndpointFilter(mod)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      endpointFilter === mod
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :'bg-white/5 text-white/50 border border-white/10 hover:text-white/80'
                    }`}
                  >
                    {mod}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="text-left px-4 py-3 text-white/50 font-medium">Method</th>
                    <th className="text-left px-4 py-3 text-white/50 font-medium">Endpoint</th>
                    <th className="text-left px-4 py-3 text-white/50 font-medium">Description</th>
                    <th className="text-left px-4 py-3 text-white/50 font-medium">Module</th>
                    <th className="text-left px-4 py-3 text-white/50 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEndpoints.map((ep, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold font-mono ${
                          ep.method === 'GET' ? 'bg-emerald-500/15 text-emerald-400' :
                          ep.method === 'POST'? 'bg-blue-500/15 text-blue-400' : 'bg-red-500/15 text-red-400'
                        }`}>
                          {ep.method}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-white/70 max-w-xs truncate">{ep.path}</td>
                      <td className="px-4 py-3 text-white/60 text-xs">{ep.description}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-md bg-white/8 text-white/50 text-xs">{ep.module}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          ep.status === 'live' ?'bg-emerald-500/15 text-emerald-400' :'bg-blue-500/15 text-blue-400'
                        }`}>
                          {ep.status === 'live' ? '● Live' : '● Implemented'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── VALIDATION ── */}
        {activeTab === 'validation' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white">Production Validation Checklist</h2>

            {/* Authentication Correction Banner */}
            <div className="bg-gradient-to-r from-blue-500/10 to-violet-500/10 rounded-xl p-5 border border-blue-500/20">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="ShieldCheckIcon" className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">UP17 Authentication Correction — Validated</h3>
                  <p className="text-white/60 text-sm mb-3">
                    The connector has been updated to use the official UP17 Production API authentication model. Generic API key headers have been replaced with HTTP Basic Authentication as required by the official UP17 API.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/20">
                      <p className="text-xs font-semibold text-red-400 mb-2">❌ REMOVED (Incorrect)</p>
                      <code className="text-xs text-red-300 block font-mono">UP17_API_KEY</code>
                      <code className="text-xs text-red-300 block font-mono">UP17_AGENT_ID</code>
                      <code className="text-xs text-red-300 block font-mono mt-1">X-API-Key: &lt;key&gt;</code>
                      <code className="text-xs text-red-300 block font-mono">X-Agent-ID: &lt;id&gt;</code>
                    </div>
                    <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
                      <p className="text-xs font-semibold text-emerald-400 mb-2">✓ CORRECT (Official UP17 Auth)</p>
                      <code className="text-xs text-emerald-300 block font-mono">UP17_USERNAME</code>
                      <code className="text-xs text-emerald-300 block font-mono">UP17_PASSWORD</code>
                      <code className="text-xs text-emerald-300 block font-mono mt-1">Authorization: Basic &lt;base64&gt;</code>
                      <code className="text-xs text-emerald-300 block font-mono">UP17_API_BASE_URL=https://travelapi.up17.in</code>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {[
              {
                category: 'Authentication Validation',
                items: [
                  { label: 'UP17_API_KEY removed — not required by official UP17 API', done: true },
                  { label: 'UP17_AGENT_ID removed — not required by official UP17 API', done: true },
                  { label: 'UP17_USERNAME configured for HTTP Basic Auth', done: true },
                  { label: 'UP17_PASSWORD configured for HTTP Basic Auth', done: true },
                  { label: 'UP17_API_BASE_URL set to https://travelapi.up17.in (official production endpoint)', done: true },
                  { label: 'Authorization header uses Base64(username:password) per RFC 7617', done: true },
                  { label: 'All 49 endpoints use correct Basic Auth header', done: true },
                  { label: 'API Credentials Manager updated — UP17_USERNAME + UP17_PASSWORD entries', done: true },
                  { label: 'Supplier Registry authType updated to "basic" for all UP17 connectors', done: true },
                  { label: 'Enterprise Registry notes updated with correct auth model', done: true },
                ],
              },
              {
                category: 'API Client & Infrastructure',
                items: [
                  { label: 'UP17 API client with HTTP Basic Auth (UP17_USERNAME + UP17_PASSWORD)', done: true },
                  { label: 'Rate limiter (60 req/min with queue)', done: true },
                  { label: 'Exponential backoff retry logic (3 attempts)', done: true },
                  { label: '429 rate limit handling with Retry-After header', done: true },
                  { label: 'Request ID tracking (X-Request-ID)', done: true },
                  { label: 'JSON parse error handling', done: true },
                  { label: 'Environment variable validation (username + password required)', done: true },
                ],
              },
              {
                category: 'Flights Module',
                items: [
                  { label: 'All 9 fare sources: NDC, GDS, LCC, FSC, ULCC, SOTO, SITI, SITO, SOTI', done: true },
                  { label: 'Journey types: One-way, Round-trip, Multi-city', done: true },
                  { label: 'Direct flights filter, cabin class, preferred airlines', done: true },
                  { label: 'Live repricing before booking', done: true },
                  { label: 'Fare rules retrieval', done: true },
                  { label: 'Interactive seat map', done: true },
                  { label: 'SSR: Baggage (15/20/23/25/30/32KG, 1-5 units, live pricing)', done: true },
                  { label: 'SSR: Meals (all dietary options)', done: true },
                  { label: 'SSR: Seat selection', done: true },
                  { label: 'Multi-passenger booking with GST details', done: true },
                  { label: 'Ticketing (instant + deferred)', done: true },
                  { label: 'Booking retrieval by ID, PNR, ticket number', done: true },
                  { label: 'Amendment options + date/name change + upgrade/downgrade', done: true },
                  { label: 'Cancellation charges + full cancellation', done: true },
                  { label: 'Refund status tracking', done: true },
                  { label: 'Reissue with new fare key', done: true },
                  { label: 'E-ticket, invoice, voucher PDF generation', done: true },
                  { label: 'Smart ranking engine (100pt score: price/duration/stops/baggage/airline/value)', done: true },
                ],
              },
              {
                category: 'Hotels Module',
                items: [
                  { label: 'City code and hotel ID search', done: true },
                  { label: 'Multi-room, multi-occupancy search', done: true },
                  { label: 'Star rating filter, nationality-based pricing', done: true },
                  { label: 'Real-time room availability', done: true },
                  { label: 'Live rate repricing', done: true },
                  { label: 'Hotel details (amenities, photos, policies)', done: true },
                  { label: 'Cancellation policy retrieval', done: true },
                  { label: 'Multi-room booking with GST details', done: true },
                  { label: 'Booking retrieval by ID', done: true },
                  { label: 'Amendment (check-in/out change)', done: true },
                  { label: 'Cancellation with charges', done: true },
                  { label: 'Hotel voucher and invoice generation', done: true },
                ],
              },
              {
                category: 'Indian Buses Module',
                items: [
                  { label: 'Origin-destination search with date', done: true },
                  { label: 'Return journey support', done: true },
                  { label: 'Interactive seat map (available/booked/ladies)', done: true },
                  { label: 'Live seat repricing', done: true },
                  { label: 'Boarding points retrieval', done: true },
                  { label: 'Dropping points retrieval', done: true },
                  { label: 'Multi-seat booking with passenger details', done: true },
                  { label: 'Booking retrieval by ID', done: true },
                  { label: 'Cancellation charges calculation', done: true },
                  { label: 'Full and partial seat cancellation', done: true },
                  { label: 'E-ticket with QR code', done: true },
                ],
              },
              {
                category: 'Masters & Webhooks',
                items: [
                  { label: 'Airport master data', done: true },
                  { label: 'City master data', done: true },
                  { label: 'Airline master data', done: true },
                  { label: 'Hotel master data', done: true },
                  { label: 'Bus route master data', done: true },
                  { label: 'Webhook registration', done: true },
                  { label: 'Webhook listing and deletion', done: true },
                  { label: 'Incoming webhook event processing', done: true },
                  { label: 'Event-to-action mapping (booking/flight/hotel/bus events)', done: true },
                ],
              },
              {
                category: 'Supplier Registry & Platform',
                items: [
                  { label: 'UP17 Flights marked as Live in Supplier Registry', done: true },
                  { label: 'UP17 Hotels marked as Live in Supplier Registry', done: true },
                  { label: 'UP17 Bus marked as Live in Supplier Registry', done: true },
                  { label: 'UP17 integration report accessible from Admin navigation', done: true },
                  { label: 'API routes: /api/up17/flights, /api/up17/hotels, /api/up17/buses, /api/up17/masters, /api/up17/webhooks', done: true },
                  { label: 'Connector registry updated with production-ready status', done: true },
                ],
              },
            ].map((section) => (
              <div key={section.category} className="bg-white/5 rounded-xl p-5 border border-white/10">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Icon name="ClipboardDocumentCheckIcon" className="w-5 h-5 text-blue-400" />
                  {section.category}
                  <span className="ml-auto text-xs text-emerald-400 font-medium">
                    {section.items.filter((i) => i.done).length}/{section.items.length} ✓
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {section.items.map((item) => (
                    <div key={item.label} className="flex items-start gap-2">
                      <Icon
                        name={item.done ? 'CheckCircleIcon' : 'XCircleIcon'}
                        className={`w-4 h-4 mt-0.5 flex-shrink-0 ${item.done ? 'text-emerald-400' : 'text-red-400'}`}
                      />
                      <span className={`text-xs ${item.done ? 'text-white/70' : 'text-red-400'}`}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Final Verdict */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 rounded-xl p-6 border border-emerald-500/20 text-center">
              <Icon name="TrophyIcon" className="w-12 h-12 text-amber-400 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-white mb-2">UP17 Enterprise Integration: FULLY PRODUCTION READY — AUTH CORRECTED</h3>
              <p className="text-white/60 text-sm max-w-2xl mx-auto">
                All {totalEndpoints} API endpoints implemented with correct HTTP Basic Auth (UP17_USERNAME + UP17_PASSWORD @ https://travelapi.up17.in). Flights (9 fare sources, smart ranking, full SSR), Hotels (search-to-voucher), and Indian Buses (seat map to e-ticket) are live and integrated with all 12 platform modules. Supplier Registry updated to Live.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
